import { Fragment, useEffect, useRef, useState } from "react";
import type { MfitCloudDashConfig } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { SkipButton } from "../../ui/SkipButton";
import type { MfitDone } from "./shared";
import { useSim } from "../../state/store";
import "./mfit.css";
import { tx } from "../../lib/tx";

/**
 * The attacker's cloud console (hacker room, attacker POV, no scoring).
 * Views: "timeline" (how the attack was built), "access" (what Alex's consent
 * gave the app + what the attacker reads + the fraud draft), "denied" (the
 * consent was refused), "network" (who is next). Narration lines are spoken
 * one at a time; each `focus` id lights up the matching element.
 */
/** Route / variant / step condition: no condition always matches. */
export function dashMatch(c: { ifEvents?: string[]; ifReplay?: boolean }, recorded: Set<string>, replay: boolean): boolean {
  if (c.ifReplay !== undefined && c.ifReplay !== replay) return false;
  if (c.ifEvents && !c.ifEvents.some((e) => recorded.has(e))) return false;
  return true;
}

function JourneyIcon({ kind }: { kind: "file" | "cloud" | "app" | "people" | "lock" }) {
  const p: Record<string, string> = {
    file: "M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6",
    cloud: "M7 18h10a4 4 0 0 0 .6-7.95A6 6 0 0 0 6.3 9.5 4.3 4.3 0 0 0 7 18z",
    app: "M4 5h16v14H4zM4 9h16M8 13h3M8 16h6",
    people: "M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 11a2.5 2.5 0 1 0 0-5M17 14c2.4.4 4 2.5 4 5",
    lock: "M6 11h12v10H6zM9 11V8a3 3 0 0 1 6 0v3",
  };
  return (
    <svg className="mfit-jr-ico" viewBox="0 0 24 24" aria-hidden="true">
      <path d={p[kind]} />
    </svg>
  );
}

export function recordedEvents(): Set<string> {
  return new Set(useSim.getState().outcomes.map((o) => o.eventId).filter(Boolean) as string[]);
}

export function MfitCloudDash({
  config: base,
  onDone,
  whatIf,
}: {
  config: MfitCloudDashConfig;
  onDone: (d: MfitDone) => void;
  /** "What would have happened" replay text (the top-right guide is hidden here). */
  whatIf?: string | null;
}) {
  const [recorded] = useState(recordedEvents);
  const variant = base.variants?.find((v) => dashMatch(v, recorded, !!whatIf));
  const config: MfitCloudDashConfig = variant ? { ...base, ...variant.config } : base;
  const steps = config.steps?.map((s) => {
    const hit = s.stateIf?.find((c) => dashMatch({ ifEvents: c.events, ifReplay: c.replay }, recorded, !!whatIf));
    return hit ? { ...s, state: hit.state, body: hit.body ?? s.body, statusLabel: hit.statusLabel ?? s.statusLabel } : s;
  });
  const [idx, setIdx] = useState(-1);
  const [done, setDone] = useState(false);
  const alive = useRef(true);
  const voice = { voice: config.voiceId || "onyx", lang: activeTtsLang() };

  useEffect(() => {
    alive.current = true;
    // every variant's lines, so the voice prerender records all of them
    [...base.narration, ...(base.variants ?? []).flatMap((v) => v.config.narration ?? [])].forEach((n) =>
      void audio.prefetchSpeech(n.text, voice),
    );
    void (async () => {
      await new Promise((r) => window.setTimeout(r, 700));
      for (let i = 0; i < config.narration.length; i++) {
        if (!alive.current) return;
        setIdx(i);
        audio.typing(5);
        await audio.speak(config.narration[i].text, voice);
        await new Promise((r) => window.setTimeout(r, 450));
      }
      if (alive.current) setDone(true);
    })();
    return () => {
      alive.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Everything whose focus id has been narrated so far is revealed; the
  // current one is highlighted.
  const order = config.narration.map((n) => n.focus).filter(Boolean) as string[];
  const revealedUpTo = (id: string) => {
    for (let i = 0; i <= idx; i++) if (config.narration[i]?.focus === id) return true;
    return false;
  };
  const isOn = (id: string) => !done && idx >= 0 && config.narration[idx]?.focus === id;
  const vis = (id: string) => (done || revealedUpTo(id) || order.indexOf(id) === -1 ? " is-in" : "") + (isOn(id) ? " is-focus" : "");

  const skip = () => {
    alive.current = false;
    audio.cancelSpeech();
    setDone(true);
  };

  return (
    <div className={`mfit-dash is-${config.view}`}>
      <div className="mfit-dash-win">
        <header className="mfit-dash-h">
          <span className="mfit-dash-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <b>{config.title}</b>
          {config.operator && <span className="mfit-dash-op">{config.operator}</span>}
          <span className="mfit-dash-sim">{tx("Simulation")}</span>
        </header>
        {whatIf && (
          <div className="mfit-dash-whatif" role="note">
            <b>{tx("What would have happened")}</b>
            <span>{whatIf}</span>
          </div>
        )}
        {config.kicker && <div className="mfit-dash-k">{config.kicker}</div>}

        <div className="mfit-dash-body">
          {(config.view === "timeline" || config.view === "denied") && config.steps && (
            <ol className="mfit-tl">
              {(steps ?? []).map((s, i) => (
                <li key={s.id} className={`mfit-tl-step is-${s.state ?? "done"}${vis(s.id)}`}>
                  <span className="mfit-tl-n">{i + 1}</span>
                  <span className="mfit-tl-c">
                    <b>{s.title}</b>
                    <small>{s.body}</small>
                  </span>
                  <span className="mfit-tl-s">
                    {s.statusLabel ?? (s.state === "failed" ? tx("Failed") : s.state === "denied" ? tx("Denied") : s.state === "success" ? tx("Access granted") : s.state === "pending" ? tx("Waiting") : tx("Done"))}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {config.view === "denied" && config.deniedNote && <div className={`mfit-denied${vis("denied")}`}>{config.deniedNote}</div>}

          {config.view === "access" && (
            <div className="mfit-acc">
              <section className="mfit-acc-grants">
                <div className="mfit-acc-sh">{tx("Cloud access: Alex")}</div>
                {(config.access ?? []).map((a) => (
                  <div key={a.id} className={`mfit-grant${vis(a.id)}`}>
                    <span className="mfit-grant-l">{a.label}</span>
                    <b>{a.value}</b>
                    <small>{a.detail}</small>
                  </div>
                ))}
              </section>
              <section className="mfit-acc-files">
                <div className="mfit-acc-sh">{tx("Exploring the mailbox and files")}</div>
                {(config.findings ?? []).map((f) => (
                  <div key={f.id} className={`mfit-find is-${f.kind}${vis(f.id)}`}>
                    <span className="mfit-find-ico" aria-hidden="true">
                      {f.kind === "mail" ? "✉" : f.kind === "sheet" ? "▦" : "▤"}
                    </span>
                    <span>
                      <b>{f.title}</b>
                      <small>{f.meta}</small>
                      {f.lines && (
                        <span className="mfit-find-lines">
                          {f.lines.map((l) => (
                            <span key={l}>{l}</span>
                          ))}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
                {config.draft && (
                  <div className={`mfit-draft${vis(config.draft.id)}`}>
                    <div className="mfit-draft-h">{tx("New message · sending as Alex")}</div>
                    <div className="mfit-draft-r">
                      <span>{tx("From")}</span>
                      {config.draft.from}
                    </div>
                    <div className="mfit-draft-r">
                      <span>{tx("To")}</span>
                      {config.draft.to}
                    </div>
                    <div className="mfit-draft-r">
                      <span>{tx("Subject")}</span>
                      {config.draft.subject}
                    </div>
                    <div className="mfit-draft-b">{config.draft.body}</div>
                  </div>
                )}
              </section>
            </div>
          )}

          {config.view === "network" && config.network && (
            <div className="mfit-net">
              <svg viewBox="0 0 760 340" className="mfit-net-svg" aria-hidden="true">
                <ellipse className="mfit-net-orbit" cx={380} cy={170} rx={250} ry={112} />
                <ellipse className="mfit-net-orbit is-inner" cx={380} cy={170} rx={125} ry={56} />
                {config.network.nodes.map((n, i) => {
                  const a = (Math.PI * 2 * i) / config.network!.nodes.length - Math.PI / 2;
                  const cosA = Math.cos(a);
                  const sinA = Math.sin(a);
                  const x = 380 + cosA * 250;
                  const y = 170 + sinA * 112;
                  const r = n.next ? 17 : 12;
                  // Labels sit outside the node: beside it on the left/right, above or below it otherwise,
                  // so neighbouring labels never overlap.
                  const side = Math.abs(cosA) > 0.5;
                  const anchor = side ? (cosA > 0 ? "start" : "end") : "middle";
                  const tx = side ? x + (cosA > 0 ? r + 10 : -(r + 10)) : x;
                  const ty1 = side ? y - 1 : sinA > 0 ? y + r + 16 : y - r - 20;
                  const ty2 = side ? y + 14 : sinA > 0 ? y + r + 31 : y - r - 5;
                  return (
                    <g key={n.name} className={`mfit-net-node${n.next ? " is-next" : ""}${vis(`node_${i}`)}`}>
                      <line x1={380} y1={170} x2={x} y2={y} />
                      {n.next && <circle className="mfit-net-ring" cx={x} cy={y} r={r} />}
                      <circle cx={x} cy={y} r={r} />
                      <circle className="mfit-net-dot" cx={x} cy={y} r={n.next ? 5 : 3.5} />
                      <text x={tx} y={ty1} textAnchor={anchor}>
                        {n.name}
                      </text>
                      <text x={tx} y={ty2} textAnchor={anchor} className="mfit-net-role">
                        {n.role}
                      </text>
                    </g>
                  );
                })}
                <g className="mfit-net-center is-in">
                  <circle cx={380} cy={170} r={24} />
                  <circle className="mfit-net-core" cx={380} cy={170} r={8} />
                  <text x={380} y={212} textAnchor="middle">
                    {config.network.center}
                  </text>
                  <text x={380} y={227} textAnchor="middle" className="mfit-net-role">
                    {config.network.centerRole}
                  </text>
                </g>
              </svg>
              <div className={`mfit-net-cap${vis("caption")}`}>{config.network.caption}</div>
            </div>
          )}

          {config.view === "journey" && config.journey && (
            <div className="mfit-jr">
              <div className="mfit-jr-row">
                {config.journey.nodes.map((n, i) => (
                  <Fragment key={n.id}>
                    {i > 0 && <span className={`mfit-jr-arrow is-${n.state ?? "reached"}${vis(n.id)}`} aria-hidden="true" />}
                    <div className={`mfit-jr-node is-${n.state ?? "reached"}${vis(n.id)}`}>
                      <JourneyIcon kind={n.icon ?? "file"} />
                      <b>{n.title}</b>
                      {n.sub && <small>{n.sub}</small>}
                      {n.tag && <em>{n.tag}</em>}
                    </div>
                  </Fragment>
                ))}
              </div>
              {config.journey.caption && <div className={`mfit-jr-cap${vis("caption")}`}>{config.journey.caption}</div>}
            </div>
          )}
        </div>

        <footer className="mfit-dash-f">
          <div className="mfit-dash-narr" aria-live="polite">
            {idx >= 0 && !done ? config.narration[idx].text : done ? config.narration[config.narration.length - 1]?.text : ""}
          </div>
          <div className="mfit-dash-btns">
            {!done && <SkipButton onClick={skip} />}
            <button type="button" className={`mfit-btn mfit-btn--primary${done ? " cf-pulse-target" : ""}`} disabled={!done} onClick={() => onDone({})}>
              {config.continueLabel ?? tx("Continue")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
